// eslint-disable-next-line import/extensions
import MarkdownRenderer from './MarkdownRenderer';
import useStore from './store';
import type { ChatMessage } from '../../types/os';
import Videos from './Multimedia/Videos/Videos';
import Images from './Multimedia/Images/Images';
import Audios from './Multimedia/Audios/Audios';
import { memo } from 'react';
import AgentThinkingLoader from './AgentThinkingLoader';
import { useAuthInfo } from '@propelauth/react';
import { ChatMessageAvatar, ChatSenderLabel, getChatUserInitials } from '../../shared/chat/ChatMessageIdentity';
import useWorkspaceStore from '../../store/workspace/workspaceStore';

interface MessageProps {
  message: ChatMessage
}

const safeFormatTime = (ts: any, timeZone?: string): string => {
  if (!ts) return '';
  let d: Date;
  if (typeof ts === 'string') {
    d = new Date(ts);
  } else if (typeof ts === 'number') {
    d = new Date(ts < 1e12 ? ts * 1000 : ts);
  } else {
    return '';
  }
  if (Number.isNaN(d.getTime())) return '';

  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  const diffHr = Math.floor(diffMs / 3600000);

  const opts: Intl.DateTimeFormatOptions = {
    hour: '2-digit',
    minute: '2-digit',
    ...(timeZone ? { timeZone } : {}),
  };

  // Humanized relative time for recent messages
  if (diffMin < 1) return 'Just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHr < 24) {
    const time = d.toLocaleTimeString([], opts);
    return diffHr === 1 ? `1h ago · ${time}` : `${diffHr}h ago · ${time}`;
  }

  // Older messages: show date + time
  const dateOpts: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    ...(timeZone ? { timeZone } : {}),
  };
  // Add year if not current year
  if (d.getFullYear() !== now.getFullYear()) {
    dateOpts.year = 'numeric';
  }
  return d.toLocaleDateString([], dateOpts);
};

const AgentMessage = ({ message }: MessageProps) => {
  const { streamingErrorMessage } = useStore();
  const wsTimezone = useWorkspaceStore((s) => s.workspace?.workspace_time_zone);

  const formattedTime = safeFormatTime(message.created_at, wsTimezone);

  const bubbleBase = 'max-w-[680px] rounded-xl rounded-tl-sm border border-border bg-quaternary px-3.5 py-2.5 text-sm leading-[1.8] text-foreground';

  let content;
  if (message.streamingError) {
    content = (
      <div className={`${bubbleBase} border border-red-200 bg-red-50 text-red-800`}>
        <p className="">
          Oops! Something went wrong while streaming. {streamingErrorMessage || 'Please try refreshing the page or try again later.'}
        </p>
      </div>
    );
  } else if (message.isThinking) {
    // Show thinking state even if there's content (initial loading message)
    content = (
      <div className={bubbleBase}>
        <div className="flex items-center gap-3">
          <div className="flex-1 text-foreground">
            <MarkdownRenderer classname="[&_p]:text-sm [&_p]:leading-[1.8] [&_li]:text-sm [&_li]:leading-[1.8] [&_ul]:text-sm [&_ol]:text-sm">
              {message.content}
            </MarkdownRenderer>
          </div>
          <div className="flex-shrink-0">
            <AgentThinkingLoader />
          </div>
        </div>
        {/* Show elapsed time if available */}
        {(message.elapsedMs ?? 0) > 0 && (
          <div className="mt-2 border-t border-border pt-2 text-xs text-muted-foreground">
            Processing for {Math.floor((message.elapsedMs ?? 0) / 1000)}s...
          </div>
        )}
      </div>
    );
  } else if (message.content) {
    content = (
      <div className={bubbleBase}>
        <div className="flex w-full flex-col gap-4">
          <MarkdownRenderer classname="[&_h1]:text-2xl [&_h2]:text-xl [&_h3]:text-lg [&_p]:text-sm [&_p]:leading-[1.8] [&_p]:text-foreground [&_li]:text-sm [&_li]:leading-[1.8] [&_li]:text-foreground [&_strong]:text-foreground [&_blockquote]:text-muted-foreground [&_blockquote]:border-l-border [&_code]:bg-muted [&_code]:text-foreground [&_pre]:bg-muted">
            {message.content}
          </MarkdownRenderer>
          {message.videos && message.videos.length > 0 && <Videos videos={message.videos} />}
          {message.images && message.images.length > 0 && <Images images={message.images} />}
          {message.audio && message.audio.length > 0 && <Audios audio={message.audio} />}
        </div>
      </div>
    );
  } else if (message.response_audio) {
    if (!message.response_audio.transcript) {
      content = (
        <div className={bubbleBase}>
          <div className="mt-1 flex items-start">
            <AgentThinkingLoader />
          </div>
        </div>
      );
    } else {
      content = (
        <div className={bubbleBase}>
          <div className="flex w-full flex-col gap-4">
            <MarkdownRenderer classname="[&_p]:text-sm [&_p]:leading-[1.8] [&_li]:text-sm [&_li]:leading-[1.8] [&_ul]:text-sm [&_ol]:text-sm">
              {message.response_audio.transcript}
            </MarkdownRenderer>
            {message.response_audio.content && message.response_audio && <Audios audio={[message.response_audio]} />}
          </div>
        </div>
      );
    }
  } else {
    content = (
      <div className={bubbleBase}>
        <div className="mt-1">
          <AgentThinkingLoader />
        </div>
      </div>
    );
  }

  return (
    <div className="flex w-full items-start gap-3">
      <ChatMessageAvatar role="assistant" className="mt-0.5 h-8 w-8 text-[9px]" />
      <div className="flex flex-col items-start gap-1">
        <ChatSenderLabel role="assistant" />
        {content}
        {formattedTime && <span className="pl-2 text-[11px] text-muted-foreground">{formattedTime}</span>}
      </div>
    </div>
  );
};

const UserMessage = memo(({ message }: MessageProps) => {
  const authInfo: any = useAuthInfo();
  const userInitials = getChatUserInitials(authInfo?.user);
  const wsTimezone = useWorkspaceStore((s) => s.workspace?.workspace_time_zone);

  const formattedTime = safeFormatTime(message.created_at, wsTimezone);

  return (
    <div className="flex w-full items-start justify-end gap-3 pt-4 text-start max-md:break-words">
      <div className="flex flex-col items-end gap-1">
        <ChatSenderLabel role="user" />
        <div className="max-w-[680px] rounded-xl rounded-tr-sm bg-accent-tint px-3.5 py-2.5 text-sm leading-[1.8] text-foreground">
          {message.content}
        </div>
        {formattedTime && <span className="pr-2 text-[11px] text-muted-foreground">{formattedTime}</span>}
      </div>
      <ChatMessageAvatar role="user" userInitials={userInitials} className="mt-0.5 h-8 w-8" />
    </div>
  );
});

AgentMessage.displayName = 'AgentMessage';
UserMessage.displayName = 'UserMessage';
export { AgentMessage, UserMessage };
